/**
 * A payout statement's `lines[]` table — moved unchanged out of `RunSheetTable` (TMMPDM-0037) so
 * a reseller settlement's expanded row shows the same detail. TMMPDM-0044: `revenueShare={false}`
 * drops the revenue-share columns (Uplift, Tenant share, Platform, Share applied) for Settlements;
 * tenant statements keep them.
 * TMMPDM-0051: every statement shows Commission from the line's `affiliateCommissionMinor`,
 * signed as the server sends it. Bounty, milestone, clawback and affiliate-commission lines dash
 * retail, wholesale and the share cells. That dash is the line's own kind, not a second field.
 * A genuine 0 on any other kind still shows $0.00.
 */
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { Amount } from '@/components/ui';
import { formatDate, formatPercentBp } from '@/format';
import type { PayoutStatementLineView, RevenueLineKind } from '@/api/tmm/types';

/** Kinds whose retail, wholesale and share cells the server writes as 0. Formatting only. */
const DASHED_KINDS = new Set(['BOUNTY', 'MILESTONE', 'CLAWBACK', 'AFFILIATE_COMMISSION']);

/** `PayoutStatementLineView.kind` is a free string on the YAML; these are its known values. */
const KIND_FALLBACK: Record<string, string> = {
  RENEWAL: 'Renewal',
  VARIABLE: 'Variable',
  REFUND: 'Refund',
  AFFILIATE_COMMISSION: 'Affiliate commission',
  ADDON_FEE: 'Add-on fee',
  // TMMAPI-0105 event lines. An unknown kind falls through to its code; `line` absent is "—".
  BOUNTY: 'Affiliate bounties',
  MILESTONE: 'Affiliate milestones',
  CLAWBACK: 'Clawback',
  RECOVERY: 'Recovered clawbacks',
};

const LINE_FALLBACK: Record<RevenueLineKind, string> = {
  BASE: 'Subscriptions',
  SEAT: 'Guest seats',
  CONNECTED: 'Connected accounts',
  AI: 'AI',
  TAX: 'Tax',
};

interface StatementLinesTableProps {
  lines: PayoutStatementLineView[];
  locale: string;
  /** The revenue-share columns; Settlements turns them off. */
  revenueShare?: boolean;
}

function LineAmount({
  dash,
  minor,
  currency,
  locale,
}: {
  dash: boolean;
  minor: number;
  currency: string;
  locale: string;
}) {
  if (dash) return '—';
  return <Amount minor={minor} currency={currency} locale={locale} />;
}

export function StatementLinesTable({
  lines,
  locale,
  revenueShare = true,
}: StatementLinesTableProps) {
  const { t } = useCopy();
  return (
    <Table size="small" aria-label={t('payouts.linesAria', 'Statement lines')}>
      <TableHead>
        <TableRow>
          <TableCell>{t('payouts.col.collected', 'Collected')}</TableCell>
          <TableCell>{t('payouts.col.kind', 'Kind')}</TableCell>
          <TableCell>{t('payouts.col.line', 'Line')}</TableCell>
          <TableCell align="right">{t('payouts.col.retail', 'Retail')}</TableCell>
          <TableCell align="right">{t('payouts.col.wholesale', 'Wholesale')}</TableCell>
          {revenueShare ? (
            <>
              <TableCell align="right">{t('payouts.col.uplift', 'Uplift')}</TableCell>
              <TableCell align="right">{t('payouts.col.tenantShare', 'Tenant share')}</TableCell>
              <TableCell align="right">{t('payouts.col.platform', 'Platform')}</TableCell>
              <TableCell>{t('payouts.col.shareApplied', 'Share applied')}</TableCell>
            </>
          ) : null}
          <TableCell align="right">{t('payouts.col.commission', 'Commission')}</TableCell>
          <TableCell>{t('payouts.col.payApiRef', 'pay-api ref')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lines.map((line, index) => {
          const dashed = DASHED_KINDS.has(line.kind);
          return (
            <TableRow key={line.id ?? `${line.kind}-${index}`}>
              <TableCell>{line.collectedAt ? formatDate(line.collectedAt, locale) : '—'}</TableCell>
              <TableCell>
                {t(`payouts.kind.${line.kind}`, KIND_FALLBACK[line.kind] ?? line.kind)}
              </TableCell>
              <TableCell>
                {line.line ? t(`revenue.line.${line.line}`, LINE_FALLBACK[line.line]) : '—'}
              </TableCell>
              <TableCell align="right">
                <LineAmount
                  dash={dashed}
                  minor={line.retailMinor}
                  currency={line.currency}
                  locale={locale}
                />
              </TableCell>
              <TableCell align="right">
                <LineAmount
                  dash={dashed}
                  minor={line.wholesaleMinor}
                  currency={line.currency}
                  locale={locale}
                />
              </TableCell>
              {revenueShare ? (
                <>
                  <TableCell align="right">
                    <LineAmount
                      dash={dashed}
                      minor={line.upliftMinor}
                      currency={line.currency}
                      locale={locale}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <LineAmount
                      dash={dashed}
                      minor={line.tenantShareMinor}
                      currency={line.currency}
                      locale={locale}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <LineAmount
                      dash={dashed}
                      minor={line.platformShareMinor}
                      currency={line.currency}
                      locale={locale}
                    />
                  </TableCell>
                  <TableCell>
                    {dashed ? '—' : formatPercentBp(line.revShareBpApplied, locale, 1)}
                  </TableCell>
                </>
              ) : null}
              <TableCell align="right">
                <Amount
                  minor={line.affiliateCommissionMinor}
                  currency={line.currency}
                  locale={locale}
                />
              </TableCell>
              <TableCell>{line.payApiPaymentRef ?? '—'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
